<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate, useParams } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { DomainService } from '@client/services/domain';
  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';
  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { generateUniqueRulename } from '@client/utils/rulenames';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  import { deepCopy, merge } from '@common/utils/objects';

  import { EditForm } from './edit-form';
  import { Signature } from './signature';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  const params = useParams();
  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  let requestParams = merge({}, USER_REQUEST_PARAMS);

  let gmailpush = {
    automatic: false,
    pushtype: 'ptp'
  };

  const defaultOutlookAddin = {
    disableRemoveDefaultSignature: false,
    disableAutoInsertSignatures: false,
    enableVisualPreview: false,
    disablePlainTextPreview: false,
    defaultSignatureMode: 'preview',
    mergeParams: false,
    ignoreblankmergefields: false,
    enableOverrides: false
  };

  let outlookaddin = { ...defaultOutlookAddin };
  let originalOutlookAddin = { ...defaultOutlookAddin };

  let group = {
    gmailpush,
    outlookaddin
  };

  let includeUsers = [];
  let excludeUsers = [];
  let parameters = [];
  let helpers = [];
  let isUpdating = false;
  let savedTick = 0;
  let original = {};
  let invalid = false;
  let isDisabled = false;

  let name = '';

  const query = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['edit-groups', $params.id],
    queryFn: async () => {
      const groupmeta = await GroupsService.getGroupById($params.id);

      if (!groupmeta) {
        redirect('/manage/groups');
        return;
      }

      const signature = await SignaturesService.getAll();

      includeUsers = excludeUsers = await UsersService.getUsers({
        ...requestParams,
        filterByDate: false,
        query: '',
        perPage: 200
      });

      const filter = signature.filter(
        ({ sendergroups }) => sendergroups.filter(({ groupname }) => groupname === $params.id).length
      );

      if (filter.length) {
        const primary = filter.find(({ blockname, status }) => blockname && status) || filter.shift();
        groupmeta.isActive = primary.status;
      }

      original = deepCopy(groupmeta);

      parameters = groupmeta.params;

      helpers = groupmeta.uihelpers || [];

      name = groupmeta.groupname;

      outlookaddin = { ...defaultOutlookAddin, ...groupmeta?.outlookaddin };

      originalOutlookAddin = deepCopy(outlookaddin);

      return groupmeta;
    }
  });

  const onUpdate = async () => {
    isUpdating = true;

    group = {
      ...group,
      uihelpers: helpers,
      params: parameters,
      outlookaddin: { ...outlookaddin },
      gmailpush: {
        automatic: gmailpush.automatic,
        pushtype: gmailpush.pushtype?.value || 'ptp'
      }
    };

    const response = await GroupsService.updateGroup(group);

    isUpdating = false;

    if (!response || response.status >= 400) {
      notify($format('label.FAILURE.UPDATE_GROUP', { values: { message: response?.message } }), true);
      return;
    }

    notify($format('label.SUCCESS.UPDATE', { values: { item: group.groupname } }));
    // Signal the dynamic-members preview to refetch against the newly saved
    // expression without the admin renavigating to the page.
    savedTick += 1;

    original = deepCopy(group);
    originalOutlookAddin = deepCopy(outlookaddin);

    if (name !== group.groupname) {
      name = group.groupname;
    }
  };

  const onChange = (items, field, exclusion) => {
    const values = ['senders', 'senderexclusions'].includes(field)
      ? (items || []).map(({ username }) => ({ username }))
      : (items || []).map(({ id }) => ({ id }));

    if (exclusion) {
      group.exclusions[field] = values;
    } else {
      group[field] = values;
    }
  };

  const onDelete = async () => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.GROUP').toLowerCase() } })
    );

    if (confirmed) {
      isUpdating = true;

      const response = await GroupsService.deleteGroup(group);

      isUpdating = false;

      if (response !== 'Empty' && response?.status >= 400) {
        notify($format('label.FAILURE.GROUP_DELETE', { values: { group: group.groupname } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: group.groupname } }));

      redirect('/manage/groups');
    }
  };

  const onClone = async () => {
    isUpdating = true;

    const groupname = generateUniqueRulename(group.groupname);

    const payload = {
      ...group,
      uihelpers: helpers,
      _id: null,
      _newObject: true,
      groupname
    };

    const response = await GroupsService.createGroup(payload);

    isUpdating = false;

    if (response?.status >= 200) {
      notify($format('label.FAILURE.GROUP_CLONE', { values: { group: group.groupname } }), 'error');
      return;
    }

    notify($format('label.SUCCESS.GROUP_CLONE', { values: { group: group.groupname } }));

    window.location.href = `${basepath}/manage/groups/${groupname}`;
  };

  $: group = $query.data;

  $: if (group) {
    if (group.gmailpush) {
      gmailpush = group.gmailpush;
    } else {
      group.gmailpush = gmailpush;
    }
  }

  let domainSettings = {};

  const initialize = async () => {
    domainSettings = await DomainService.getSettings();
    return domainSettings;
  };

  onMount(async () => await initialize());

  $: isDisabled =
    (JSON.stringify(group) === JSON.stringify(original) &&
      JSON.stringify(parameters) === JSON.stringify(group.params || []) &&
      JSON.stringify(helpers) === JSON.stringify(original.uihelpers || []) &&
      JSON.stringify(outlookaddin) === JSON.stringify(originalOutlookAddin)) ||
    invalid;
  $: isActive = group?.isActive;
</script>

<div class="page group-edit">
  <Loader backdrop show={$query.isLoading || $query.isFetching || isUpdating} />
  <div class="page-header">
    <Container fluid class="page-header-inner">
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/manage/groups" use:link>{$format('label.GROUPS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDIT_GROUP')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="9" sm="6" md="6">
          <h1 class="page-title">
            {name}

            {#if isActive && !$appStore.mobile}
              <div class="status">{$format('label.ROUTED')}</div>
            {/if}
          </h1>
        </Col>
        <Col xs="3" sm="6" md="6" class="text-right">
          <ButtonDropdown class="hide--mobile">
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={onClone}>{$format('label.CLONE')}</DropdownItem>
              <DropdownItem divider />
              <DropdownItem on:click={onDelete} disabled={isActive}>{$format('label.DELETE')}</DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>

          <div class="action-divider hide--mobile"></div>

          <Button color="secondary" class="hide--mobile me-2" on:click={() => redirect(`/manage/groups`)}>
            {$format('label.CANCEL')}
          </Button>

          <Button color="primary" disabled={isDisabled} on:click={onUpdate}>{$format('label.UPDATE')}</Button>
        </Col>
      </Row>
    </Container>
  </div>

  {#if !$query.isFetching}
    <div class="page-content">
      <Container fluid>
        <Row class="pt-4">
          <Col>
            <div class="groups-layout">
              {#if group}
                <div class="group-edit">
                  <EditForm
                    bind:group
                    bind:invalid
                    bind:includeUsers
                    bind:excludeUsers
                    bind:parameters
                    bind:gmailpush
                    bind:outlookaddin
                    bind:helpers
                    {domainSettings}
                    {onChange}
                    {savedTick}
                  />

                  <div class="section">
                    <div class="delete-group">
                      <h2>{$format('label.DELETE')}</h2>
                      <p>
                        {$format('label.DELETE_GROUP_CONFIRMATION')}
                      </p>
                      <Button color="danger" class="mt-3" disabled={isActive} on:click={onDelete}
                        >{$format('label.DELETE')}</Button
                      >
                    </div>
                  </div>
                </div>
                <div class="section">
                  <h2>{$format('label.ROUTES')}</h2>
                  <Signature bind:group hasChanges={!isDisabled} groupId={$params.id} />
                </div>
              {/if}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  {/if}
</div>

<style lang="scss" src="./edit.scss"></style>
